import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CheckCircle, Clock, AlertCircle, FileSignature, Loader2, Trash2 } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { formatDateTime } from '../../utils/dateUtils';
import { toast } from '../../lib/toast';
import {
  TELEMED_AGREEMENT_TITLE,
  TELEMED_AGREEMENT_VERSION,
  TELEMED_AGREEMENT_SECTIONS,
} from '../../content/telemedAgreement';

/**
 * TelemedAgreementSign
 *
 * Onboarding step for telemed-only clinics (no NPI, prescribing through the
 * doctor network). Instead of the license/selfie ID-proofing wizard, these
 * clinics e-sign a single combined Clinic Services Agreement + BAA — they touch
 * PHI via the network. Signing records the executed document (signature +
 * agreement version) and moves the account to `submitted`; an admin still
 * approves it, exactly like the standard ID-proofing path.
 */

interface Props {
  physicianId: string;
  onComplete: () => void;
  onCancel: () => void;
}

const CANVAS_WIDTH = 500;
const CANVAS_HEIGHT = 180;

export const TelemedAgreementSign: React.FC<Props> = ({ physicianId, onComplete, onCancel }) => {
  const [statusLoading, setStatusLoading] = useState(true);
  const [backendStatus, setBackendStatus] = useState<{
    status: string;
    submittedAt?: string;
    verifiedAt?: string;
    agreementAccepted?: boolean;
  } | null>(null);

  const [agreed, setAgreed] = useState(false);
  const [signatureBlob, setSignatureBlob] = useState<Blob | null>(null);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawingRef = useRef(false);

  // Fetch current status so an already-submitted/approved/rejected clinic sees
  // the right screen instead of the signing form again.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getIdProofingStatus(physicianId);
        if (!cancelled && res.success && res.data) {
          setBackendStatus({
            status: res.data.idProofingStatus,
            submittedAt: res.data.submittedAt,
            verifiedAt: (res.data as any).approvedAt || (res.data as any).verifiedAt,
            agreementAccepted: (res.data as any).progress?.agreementAccepted === true,
          });
        }
      } catch {
        // Non-fatal: fall through to the signing form.
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [physicianId]);

  // Init canvas drawing context once mounted on the signing form.
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
    ctxRef.current = ctx;
  }, []);

  const pointFromEvent = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(clientX - rect.left, canvas.width)),
      y: Math.max(0, Math.min(clientY - rect.top, canvas.height)),
    };
  };

  const startAt = (x: number, y: number) => {
    const ctx = ctxRef.current;
    if (!ctx || submitting) return;
    drawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const moveAt = (x: number, y: number) => {
    const ctx = ctxRef.current;
    if (!ctx || !drawingRef.current) return;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasStrokes(true);
  };
  const endStroke = () => {
    const ctx = ctxRef.current;
    if (!ctx || !drawingRef.current) return;
    ctx.closePath();
    drawingRef.current = false;
    // Auto-capture the current canvas as the signature blob.
    canvasRef.current?.toBlob((blob) => setSignatureBlob(blob), 'image/png', 0.8);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
    setSignatureBlob(null);
  };

  const canSubmit = agreed && !!signatureBlob && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const sig = await apiClient.uploadSignature(physicianId, signatureBlob!, TELEMED_AGREEMENT_VERSION);
      if (!sig.success) {
        toast.error(`Could not save your signature: ${sig.error || 'please try again'}`, { duration: 7000 });
        setSubmitting(false);
        return;
      }
      const res = await apiClient.submitIdProofing(physicianId);
      const alreadySubmitted = !res.success && /already submitted/i.test(res.error || '');
      if (res.success || alreadySubmitted) {
        toast.success('Agreement signed and submitted for review.');
        onComplete();
      } else {
        toast.error(`Submission failed: ${res.error || 'please try again'}`, { duration: 7000 });
        setSubmitting(false);
      }
    } catch {
      toast.error('Submission failed. Please try again.', { duration: 7000 });
      setSubmitting(false);
    }
  };

  if (statusLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  // Already-submitted / approved / rejected screens.
  if (backendStatus?.status === 'submitted') {
    return (
      <StatusCard
        icon={<Clock className="h-16 w-16 text-blue-600 mx-auto mb-4" />}
        title="Agreement Submitted"
        subtitle="Your signed agreement has been submitted for review."
        boxClass="bg-blue-50 border-blue-200 text-blue-800"
        boxTitle="What happens next?"
        lines={[
          'Our team will review your signed Clinic Services Agreement & BAA.',
          'This typically takes 1–2 business days.',
          'You’ll get an email when your account is approved.',
        ]}
        footer={backendStatus.submittedAt ? `Submitted: ${formatDateTime(backendStatus.submittedAt, { month: 'long' })}` : undefined}
        buttonLabel="Back to Dashboard"
        buttonClass="bg-blue-600 text-white hover:bg-blue-700"
        onClick={onCancel}
      />
    );
  }

  // Approved WITHOUT an agreement on file happens when an admin approves the ID
  // step before the clinic signs (force-verify, network assignment). That clinic
  // is still locked until it signs, so show the form, not "you're all set".
  if (backendStatus?.status === 'approved' && backendStatus.agreementAccepted) {
    return (
      <StatusCard
        icon={<CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />}
        title="Agreement Approved"
        subtitle="Your clinic agreement has been approved."
        boxClass="bg-green-50 border-green-200 text-green-800"
        boxTitle="🎉 You’re all set"
        lines={['Your account can now place orders through the doctor network.']}
        footer={backendStatus.verifiedAt ? `Approved: ${formatDateTime(backendStatus.verifiedAt, { month: 'long' })}` : undefined}
        buttonLabel="Go to Dashboard"
        buttonClass="bg-green-600 text-white hover:bg-green-700"
        onClick={onCancel}
      />
    );
  }

  if (backendStatus?.status === 'rejected') {
    return (
      <StatusCard
        icon={<AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />}
        title="Needs Attention"
        subtitle="Your agreement needs additional information or corrections."
        boxClass="bg-red-50 border-red-200 text-red-800"
        boxTitle="What to do next"
        lines={['Please check your email or contact support for specific instructions.']}
        buttonLabel="Back to Dashboard"
        buttonClass="bg-gray-200 text-gray-800 hover:bg-gray-300"
        onClick={onCancel}
      />
    );
  }

  // Signing form.
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileSignature className="h-6 w-6 text-blue-600" /> Clinic Agreement
            </h1>
            <p className="text-gray-600 mt-1">
              Review and sign to activate your telemedicine clinic account.
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 px-3 py-1 rounded">
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            Because your clinic prescribes through the doctor network and will handle patient
            information, you don’t upload an ID — instead, please review and sign the agreement below.
          </span>
        </div>

        {/* Agreement document */}
        <div className="bg-white border rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">{TELEMED_AGREEMENT_TITLE}</h2>
            <p className="text-xs text-gray-400 mt-0.5">Version {TELEMED_AGREEMENT_VERSION}</p>
          </div>
          <div className="px-6 py-4 max-h-[420px] overflow-y-auto space-y-5">
            {TELEMED_AGREEMENT_SECTIONS.map((section) => (
              <div key={section.heading}>
                <h3 className="text-sm font-semibold text-gray-900 mb-1.5">{section.heading}</h3>
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="text-sm text-gray-600 leading-relaxed mb-2">{p}</p>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Accept */}
        <label className="flex items-start gap-3 bg-white border rounded-lg p-4 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            disabled={submitting}
            onChange={(e) => setAgreed(e.target.checked)}
            className="h-4 w-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            I have read and agree, on behalf of the clinic, to the{' '}
            <strong>Clinic Services Agreement</strong> and <strong>Business Associate Agreement</strong> above,
            and I am authorized to bind the clinic.
          </span>
        </label>

        {/* Signature */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Signature</h3>
            <button
              type="button"
              onClick={clearSignature}
              disabled={submitting || !hasStrokes}
              className={`inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 ${
                submitting || !hasStrokes ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
              }`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-3">Draw your signature in the box using your mouse or finger.</p>
          <div className="flex justify-center">
            <canvas
              ref={(el) => { canvasRef.current = el; if (el) initCanvas(); }}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              className={`border border-gray-400 rounded bg-white cursor-crosshair max-w-full ${submitting ? 'opacity-50 pointer-events-none' : 'hover:border-blue-400'}`}
              style={{ touchAction: 'none' }}
              onMouseDown={(e) => { const p = pointFromEvent(e.clientX, e.clientY); startAt(p.x, p.y); }}
              onMouseMove={(e) => { const p = pointFromEvent(e.clientX, e.clientY); moveAt(p.x, p.y); }}
              onMouseUp={endStroke}
              onMouseLeave={endStroke}
              onTouchStart={(e) => { const t = e.touches[0]; if (t) { const p = pointFromEvent(t.clientX, t.clientY); startAt(p.x, p.y); } }}
              onTouchMove={(e) => { e.preventDefault(); const t = e.touches[0]; if (t) { const p = pointFromEvent(t.clientX, t.clientY); moveAt(p.x, p.y); } }}
              onTouchEnd={endStroke}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`px-6 py-2.5 rounded-lg text-white font-medium ${
              canSubmit ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? (
              <span className="flex items-center"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting…</span>
            ) : (
              'Sign & Submit'
            )}
          </button>
        </div>
        {!canSubmit && !submitting && (
          <p className="text-right text-xs text-gray-400 -mt-3">
            {!agreed ? 'Accept the agreement and add your signature to continue.' : !signatureBlob ? 'Add your signature to continue.' : ''}
          </p>
        )}
      </div>
    </div>
  );
};

// Small shared status screen used for submitted/approved/rejected states.
const StatusCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  boxClass: string;
  boxTitle: string;
  lines: string[];
  footer?: string;
  buttonLabel: string;
  buttonClass: string;
  onClick: () => void;
}> = ({ icon, title, subtitle, boxClass, boxTitle, lines, footer, buttonLabel, buttonClass, onClick }) => (
  <div className="min-h-screen bg-gray-50">
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
        <div className="mb-6">
          {icon}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
          <p className="text-gray-600">{subtitle}</p>
        </div>
        <div className={`border rounded-lg p-4 mb-6 text-left ${boxClass}`}>
          <h3 className="font-medium mb-2">{boxTitle}</h3>
          <ul className="text-sm space-y-1">
            {lines.map((l, i) => <li key={i}>• {l}</li>)}
          </ul>
        </div>
        {footer && <p className="text-sm text-gray-500 mb-6">{footer}</p>}
        <button onClick={onClick} className={`px-6 py-2 rounded-lg font-medium transition-colors ${buttonClass}`}>
          {buttonLabel}
        </button>
      </div>
    </div>
  </div>
);

export default TelemedAgreementSign;
